// pages/Departments.jsx — CRUD for departments + head-of-department assignment
import React, { useEffect, useState } from 'react';
import api, { getAvatarUrl } from '../lib/api';
import { usePermission, Can } from '../lib/permissions';
import Portal from '../components/Portal';
import { Plus, X, Building2, Users, Trash2, Edit, UserPlus, UserMinus } from 'lucide-react';

const COLORS = ['#0079BF', '#D29034', '#519839', '#B04632', '#89609E', '#CD5A91', '#4BBF6B', '#00AECC'];

export default function Departments() {
  const { has } = usePermission();
  const canManage = has('departments.manage');
  const [depts, setDepts] = useState([]);
  const [members, setMembers] = useState([]);
  const [editing, setEditing] = useState(null); // dept or 'new' or null
  const [viewing, setViewing] = useState(null); // dept for members modal

  const load = async () => {
    const [d, m] = await Promise.all([api.get('/settings/departments'), api.get('/team')]);
    setDepts(d.data.departments); setMembers(m.data.members);
  };
  useEffect(() => { load(); }, []);

  const del = async (d) => {
    if (!confirm(`Delete "${d.name}"? Users in this department will be un-assigned.`)) return;
    await api.delete(`/settings/departments/${d.id}`);
    load();
  };

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="departments-page">
      <div className="flex items-center justify-between mb-6" style={{ color: 'var(--trello-text)' }}>
        <div>
          <div className="flex items-center gap-2"><Building2 size={24} /><h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Departments</h1></div>
          <p style={{ opacity: 0.85, marginTop: 4, fontSize: 13 }}>Organize users into departments. Each department has a manager (head) who can see their team&apos;s activity.</p>
        </div>
        <Can perm="departments.manage">
          <button className="tm-btn" onClick={() => setEditing('new')} data-testid="new-dept-btn" style={{ padding: '8px 16px', borderRadius: 10 }}>
            <Plus size={14} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} />New department
          </button>
        </Can>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="dept-grid">
        {depts.length === 0 && (
          <div className="tm-stat-card" style={{ borderLeft: 'none', gridColumn: '1/-1', textAlign: 'center', color: 'var(--trello-text-muted)' }}>
            No departments yet. {canManage && 'Create your first one!'}
          </div>
        )}
        {depts.map((d) => (
          <div key={d.id} className="tm-stat-card" style={{ borderLeft: `4px solid ${d.color}`, padding: 14, cursor: 'pointer', transition: 'transform 120ms, box-shadow 120ms' }}
            onClick={() => setViewing(d)}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.14)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = ''; }}
            data-testid={`dept-card-${d.id}`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{d.name}</div>
                <div style={{ fontSize: 12, color: 'var(--trello-text-muted)' }}>{d.description || 'No description'}</div>
              </div>
              {canManage && (
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button className="tm-btn-ghost" onClick={() => setEditing(d)} data-testid={`edit-dept-${d.id}`}><Edit size={14} /></button>
                  <button className="tm-btn-ghost" onClick={() => del(d)} data-testid={`del-dept-${d.id}`} style={{ color: '#DE350B' }}><Trash2 size={14} /></button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2" style={{ fontSize: 12, color: 'var(--trello-text-muted)' }}>
              <Users size={12} /><b>{d.user_count || 0}</b> members
            </div>
            {d.head_name && (
              <div className="mt-2 flex items-center gap-2 p-2" style={{ background: 'var(--trello-list-bg)', borderRadius: 4 }}>
                <div className="tm-avatar" style={{ width: 24, height: 24, fontSize: 10, background: d.head_avatar ? `url(${d.head_avatar}) center/cover` : d.color }}>
                  {!d.head_avatar && d.head_name.slice(0, 2).toUpperCase()}
                </div>
                <div style={{ flex: 1, fontSize: 12 }}>
                  <div style={{ fontWeight: 700 }}>{d.head_name}</div>
                  <div style={{ color: 'var(--trello-text-muted)', fontSize: 10 }}>Department head</div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && (
        <DeptModal dept={editing === 'new' ? null : editing} members={members}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {viewing && (
        <MembersModal dept={viewing} members={members} canManage={canManage}
          onClose={() => setViewing(null)} onChanged={load} />
      )}
    </div>
  );
}

// ============ Members modal (view + add + remove) ============
function MembersModal({ dept, members, canManage, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [addId, setAddId] = useState('');

  const inDept = members.filter((m) => m.department_id === dept.id);
  const outsideDept = members.filter((m) => m.department_id !== dept.id);

  const addUser = async () => {
    if (!addId) return;
    setBusy(true);
    try { await api.patch(`/team/${addId}`, { department_id: dept.id }); setAddId(''); onChanged(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };
  const removeUser = async (uid) => {
    if (!confirm('Remove this user from the department?')) return;
    try { await api.patch(`/team/${uid}`, { department_id: null }); onChanged(); }
    catch (e) { alert(e.response?.data?.error || 'Failed'); }
  };

  return (
    <Portal>
    <div className="tm-modal-overlay" onClick={onClose}>
      <div className="tm-modal" style={{ maxWidth: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()} data-testid="dept-members-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <span style={{ width: 14, height: 14, borderRadius: 3, background: dept.color, display: 'inline-block' }} />
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{dept.name}</h2>
            </div>
            <div style={{ fontSize: 12, color: 'var(--trello-text-muted)', marginTop: 2 }}>{dept.description || 'No description'}</div>
          </div>
          <button className="tm-btn-ghost" onClick={onClose} data-testid="dept-members-close"><X size={18} /></button>
        </div>

        {dept.head_name && (
          <div className="flex items-center gap-2 p-2 mb-3" style={{ background: 'var(--trello-list-bg)', borderRadius: 4 }}>
            <div className="tm-avatar" style={{ width: 32, height: 32, fontSize: 12, background: dept.head_avatar ? `url(${dept.head_avatar}) center/cover` : dept.color }}>
              {!dept.head_avatar && dept.head_name.slice(0, 2).toUpperCase()}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{dept.head_name}</div>
              <div style={{ color: 'var(--trello-text-muted)', fontSize: 11 }}>Department head</div>
            </div>
          </div>
        )}

        <div className="tm-label" style={{ marginBottom: 6 }}>Members ({inDept.length})</div>
        <div style={{ overflowY: 'auto', flex: 1, background: 'var(--trello-card-bg)', border: '1px solid var(--trello-border)', borderRadius: 6 }}>
          {inDept.length === 0 && (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--trello-text-muted)', fontSize: 13 }}>No members in this department yet.</div>
          )}
          {inDept.map((m) => (
            <div key={m.id} className="flex items-center gap-3 p-2" style={{ borderBottom: '1px solid var(--trello-border)' }} data-testid={`dept-member-${m.id}`}>
              <div className="tm-avatar" style={{ width: 28, height: 28, fontSize: 11, background: m.avatar_url ? `url(${getAvatarUrl(m.avatar_url)}) center/cover` : (m.avatar_color || 'var(--trello-text-muted)') }}>
                {!m.avatar_url && (m.full_name || m.email).slice(0, 2).toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{m.full_name}</div>
                <div style={{ fontSize: 11, color: 'var(--trello-text-muted)' }}>{m.email} · {m.role}</div>
              </div>
              {canManage && m.id !== dept.head_id && (
                <button className="tm-btn-ghost" onClick={() => removeUser(m.id)} style={{ color: '#DE350B' }} data-testid={`dept-remove-${m.id}`} title="Remove from department">
                  <UserMinus size={14} />
                </button>
              )}
            </div>
          ))}
        </div>

        {canManage && (
          <div className="mt-3 flex items-end gap-2" data-testid="dept-add-picker">
            <div style={{ flex: 1 }}>
              <div className="tm-label">Add member</div>
              <select className="tm-input" value={addId} onChange={(e) => setAddId(e.target.value)} data-testid="dept-add-select">
                <option value="">— Pick a user —</option>
                {outsideDept.map((m) => (
                  <option key={m.id} value={m.id}>{m.full_name} · {m.email} ({m.role})</option>
                ))}
              </select>
            </div>
            <button className="tm-btn" onClick={addUser} disabled={!addId || busy} data-testid="dept-add-btn">
              <UserPlus size={14} style={{ display: 'inline', marginRight: 4, verticalAlign: -2 }} />
              {busy ? 'Adding…' : 'Add'}
            </button>
          </div>
        )}
      </div>
    </div>
    </Portal>
  );
}

function DeptModal({ dept, members, onClose, onSaved }) {
  const isNew = !dept;
  const [form, setForm] = useState({
    name: dept?.name || '',
    description: dept?.description || '',
    color: dept?.color || COLORS[0],
    head_id: dept?.head_id || '',
  });
  const [busy, setBusy] = useState(false);
  const managers = members.filter((m) => m.role === 'leader' || m.role === 'manager');

  const save = async () => {
    if (!form.name.trim()) return alert('Name required');
    setBusy(true);
    try {
      const payload = { ...form, head_id: form.head_id || null };
      if (isNew) await api.post('/settings/departments', payload);
      else await api.patch(`/settings/departments/${dept.id}`, payload);
      onSaved();
    } catch (e) { alert(e.response?.data?.error || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Portal>
    <div className="tm-modal-overlay" onClick={onClose}>
      <div className="tm-modal" style={{ maxWidth: 500 }} onClick={(e) => e.stopPropagation()} data-testid="dept-modal">
        <div className="flex items-center justify-between mb-3">
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{isNew ? 'New department' : `Edit — ${dept.name}`}</h2>
          <button className="tm-btn-ghost" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <div className="tm-label">Name</div>
            <input className="tm-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="dept-name" />
          </div>
          <div>
            <div className="tm-label">Description</div>
            <textarea className="tm-input" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="dept-desc" />
          </div>
          <div>
            <div className="tm-label">Color</div>
            <div className="flex gap-2 flex-wrap">
              {COLORS.map((c) => (
                <button key={c} onClick={() => setForm({ ...form, color: c })}
                  style={{ width: 30, height: 30, borderRadius: 6, background: c, border: form.color === c ? '3px solid var(--trello-text)' : '2px solid #fff', cursor: 'pointer' }}
                  data-testid={`dept-color-${c}`} />
              ))}
            </div>
          </div>
          <div>
            <div className="tm-label">Department head (manager)</div>
            <select className="tm-input" value={form.head_id} onChange={(e) => setForm({ ...form, head_id: e.target.value })} data-testid="dept-head">
              <option value="">— No head assigned —</option>
              {managers.map((m) => <option key={m.id} value={m.id}>{m.full_name} ({m.role})</option>)}
            </select>
            <div style={{ fontSize: 11, color: 'var(--trello-text-muted)', marginTop: 4 }}>
              The department head will see all users in this department in their Dashboard, Reports, and Rewards.
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button className="tm-btn-ghost" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="tm-btn" onClick={save} disabled={busy} data-testid="save-dept">{busy ? 'Saving…' : (isNew ? 'Create' : 'Save')}</button>
        </div>
      </div>
    </div>
    </Portal>
  );
}
